import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  input,
  signal,
  ElementRef,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-pdf-html-preview',
  imports: [MatIconModule],
  templateUrl: './pdf-html-preview.component.html',
  styleUrl: './pdf-html-preview.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfHtmlPreviewComponent implements OnInit, OnChanges, OnDestroy {
  readonly iframe = viewChild<ElementRef<HTMLIFrameElement>>('iframe');

  html = input.required<string>();

  currentPage = signal<number | undefined>(undefined);
  totalPages = signal<number | undefined>(undefined);

  private domainName = window.origin;
  private messsageEventListner;
  private supportsBreakBefore: boolean;
  private lastHtml: string;

  ngOnInit(): void {
    // enable workarounds for browsers with lacking break-before: column support (e.g. Firefox)
    this.supportsBreakBefore = CSS.supports('break-before', 'column');

    const setIframeSizeFn = this.setIframeSize.bind(this);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ref = this;
    this.messsageEventListner = window.addEventListener(
      'message',
      function (event) {
        if (
          event.origin === ref.domainName &&
          event.data.name === 'pagedJsInit'
        ) {
          ref.currentPage.set(1);
          ref.totalPages.set(event.data.totalPages);

          // set parent iframe size
          setIframeSizeFn(event.data.width, event.data.height, event.data.unit);
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentHtml = this.html();
    if ('html' in changes && currentHtml && currentHtml !== this.lastHtml) {
      this.lastHtml = currentHtml;
      setTimeout(() => {
        this.appendNewIframe();
      }, 0);
    }
  }

  appendNewIframe() {
    const iframe = this.iframe();
    const parentElement: HTMLElement = iframe.nativeElement.parentElement;
    iframe.nativeElement.remove();

    const newIframe = document.createElement('iframe');

    newIframe.setAttribute('width', '100%');
    newIframe.setAttribute('height', '100%');
    newIframe.classList.add('pdf-preview-iframe', 'mat-elevation-z2');

    iframe.nativeElement = newIframe;

    parentElement.appendChild(newIframe);

    const iframeDoc =
      newIframe.contentDocument ?? newIframe.contentWindow?.document;

    if (!iframeDoc) return;

    iframeDoc.open();
    iframeDoc.write(this.html());

    const iframeHead = iframeDoc.head;
    const domainName = this.domainName;

    // Add Firefox workaround style if needed
    if (!this.supportsBreakBefore) {
      const style = iframeDoc.createElement('style');
      style.textContent = `
        .menu .page-break:not(:first-child)::before {
          content: '';
          display: block;
          margin-bottom: 100vh;
        }
      `;
      iframeHead.appendChild(style);
    }

    // Inline script for CSS patches - MUST run synchronously before pagedjs loads
    const inlinePatchScript = iframeDoc.createElement('script');
    inlinePatchScript.textContent = `
      // Patch CSS for pagedjs compatibility
      document.querySelectorAll('style').forEach(function(style) {
        // Detect "X / Y" page number format
        if (style.textContent.includes("' / ' counter(pages)") || style.textContent.includes("' / 'counter(pages)")) {
          window.__pagedjsShowTotalPages = true;
          window.__pagedjsUsePagesCounter = true;
        }
        // Remove target-counter (pagedjs can't parse it)
        if (style.textContent.includes('target-counter')) {
          if (style.textContent.includes("' / ' target-counter") || style.textContent.includes("' / 'target-counter")) {
            window.__pagedjsShowTotalPages = true;
          }
          style.textContent = style.textContent.replace(/ ' \\/ ' target-counter\\(url\\(#end\\), page\\)/g, '');
        }
        // Extract and remove counter-reset. The @page :first rule is the
        // pagenumbers_start offset; other counter-reset: page occurrences in
        // menu CSS must never overwrite an already-extracted offset.
        var firstRuleMatch = style.textContent.match(/@page\\s*:first\\s*\\{[^}]*counter-reset:\\s*page\\s+(-?\\d+)/);
        var anyMatch = style.textContent.match(/counter-reset:\\s*page\\s+(-?\\d+)/);
        if (firstRuleMatch) {
          window.__pagedjsPageNumberOffset = parseInt(firstRuleMatch[1], 10);
          window.__pagedjsOffsetFromFirstRule = true;
        } else if (anyMatch && !window.__pagedjsOffsetFromFirstRule && window.__pagedjsPageNumberOffset === undefined) {
          window.__pagedjsPageNumberOffset = parseInt(anyMatch[1], 10);
        }
        if (firstRuleMatch || anyMatch) {
          style.textContent = style.textContent.replace(/@page\\s*:first\\s*\\{\\s*counter-reset:\\s*page\\s+-?\\d+;?\\s*\\}/g, '');
        }
      });
      // Patch browser APIs for error suppression
      var wrapCb = function(fn) {
        return function() {
          try { return fn.apply(this, arguments); }
          catch(e) { if (e && e.message && e.message.includes('getAttribute')) { return; } throw e; }
        };
      };
      var OrigRO = window.ResizeObserver;
      window.ResizeObserver = function(cb) { return new OrigRO(wrapCb(cb)); };
      window.ResizeObserver.prototype = OrigRO.prototype;
      var origRAF = window.requestAnimationFrame;
      window.requestAnimationFrame = function(cb) { return origRAF.call(window, wrapCb(cb)); };
      // Disable pagedjs auto-start
      window.PagedConfig = { auto: false };
    `;
    iframeHead.appendChild(inlinePatchScript);

    // Close document before loading external scripts
    iframeDoc.close();

    // Chain script loading: pagedjs -> handler -> init
    const pagedjsScript = iframeDoc.createElement('script');
    pagedjsScript.src =
      'https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js';
    pagedjsScript.onload = function () {
      // Load handler script after pagedjs is ready. The asset is deployed
      // unhashed, so bump the version query on changes to bust browser caches.
      const handlerScript = iframeDoc.createElement('script');
      handlerScript.src = '/assets/scripts/pagedjs-patches.js?v=4';
      handlerScript.onload = function () {
        // Initialize after handler is loaded
        const iframeWindow = iframeDoc.defaultView as Window & {
          initPagedjsPreview?: (domain: string) => void;
        };
        if (iframeWindow?.initPagedjsPreview) {
          iframeWindow.initPagedjsPreview(domainName);
        }
      };
      iframeHead.appendChild(handlerScript);
    };
    iframeHead.appendChild(pagedjsScript);
  }

  setIframeSize(width: number, height: number, unit: string): void {
    const iframe = this.iframe().nativeElement;
    iframe.classList.add('rendered');
    iframe.style.height = `${height}${unit}`;
    iframe.style.width = `${width}${unit}`;
  }

  showNextPdfPage(): void {
    if (this.currentPage() + 1 > this.totalPages()) return;
    this.iframe().nativeElement.contentWindow.postMessage(
      { name: 'changepage', page: this.currentPage() + 1 },
      this.domainName,
    );
    this.currentPage.set(this.currentPage() + 1);
  }

  showPreviousPdfPage(): void {
    if (this.currentPage() - 1 === 0) return;
    this.iframe().nativeElement.contentWindow.postMessage(
      { name: 'changepage', page: this.currentPage() - 1 },
      this.domainName,
    );
    this.currentPage.set(this.currentPage() - 1);
  }

  ngOnDestroy(): void {
    if (this.messsageEventListner) {
      window.removeEventListener('message', this.messsageEventListner);
    }
  }
}
